import type { LocalInventoryFilters, NumericFilterCondition } from '@/types';
import { isNumericConditionActive } from './apply-numeric-condition';

/** Map sidebar filter ID to table column ID (for locate/highlight). */
export const FILTER_ID_TO_COLUMN_ID: Record<string, string> = {
  accountId: 'accountId',
  counterparty: 'counterparty',
  currency: 'currency',
  exchange: 'exchange',
  caStatus: 'caStatus',
  caType: 'caType',
  brokerAccountName: 'brokerAccountName',
  brokerSettlementCounterparty: 'brokerSettlementCounterparty',
  quantity: 'quantity',
  settledQuantity: 'settledQuantity',
  quantityT1: 'quantityT1',
  quantityT2: 'quantityT2',
  quantityTx: 'quantityTx',
  leverageRate: 'leverageRate',
  valueEUR: 'valueEUR',
  benchmarkFee: 'benchmarkFee',
  activeUtilisation: 'activeUtilisation',
  quantityOnLoan: 'quantityOnLoan',
  rebateRate: 'rebateRate',
  currencyBenchmark: 'currencyBenchmark',
  dailyPotential: 'dailyPotentialEUR',
  adtv30D: 'adtv30D',
  adtvMultiple: 'adtvMultiple',
  adtvAdjusted: 'adtvAdjustedPotentialEUR',
  l2cpGroup: 'lentToCounterparties',
  pendingTransfersOut: 'pendingTransfers',
  cimbEq2: 'cimbEq2Quantity',
  cimbSf: 'cimbSfQuantity',
  cimbSf3: 'cimbSf3Quantity',
  velocityL: 'velocityLQuantity',
  velocityM: 'velocityMQuantity',
  exDate: 'exDate',
};

/** Map table column ID to sidebar filter ID (for header click -> focus filter). */
export const COLUMN_ID_TO_FILTER_ID: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [filterId, columnId] of Object.entries(FILTER_ID_TO_COLUMN_ID)) {
    if (!m[columnId]) m[columnId] = filterId;
  }
  m.dailyPotentialEUR = 'dailyPotential';
  m.adtvAdjustedPotentialEUR = 'adtvAdjusted';
  m.lentToCounterparties = 'l2cpGroup';
  m.l2cpQuantity = 'l2cpGroup';
  m.l2cpAmountEur = 'l2cpGroup';
  m.l2cpRate = 'l2cpGroup';
  m.l2cpGrossRate = 'l2cpGroup';
  m.l2cpSnpDelta = 'l2cpGroup';
  return m;
})();

export const CONDITION_KEYS: Record<string, keyof LocalInventoryFilters> = {
  quantity: 'quantityCondition',
  settledQuantity: 'settledQuantityCondition',
  quantityT1: 'quantityT1Condition',
  quantityT2: 'quantityT2Condition',
  quantityTx: 'quantityTxCondition',
  leverageRate: 'leverageRateCondition',
  valueEUR: 'valueEURCondition',
  currencyBenchmark: 'currencyBenchmarkCondition',
  dailyPotential: 'dailyPotentialCondition',
  adtv30D: 'adtv30DCondition',
  adtvMultiple: 'adtvMultipleCondition',
  adtvAdjusted: 'adtvAdjustedCondition',
  benchmarkFee: 'benchmarkFeeCondition',
  activeUtilisation: 'activeUtilisationCondition',
  quantityOnLoan: 'quantityOnLoanCondition',
  rebateRate: 'rebateRateCondition',
  pendingTransfersOut: 'pendingTransfersOutCondition',
  cimbEq2: 'cimbEq2QuantityCondition',
  cimbSf: 'cimbSfQuantityCondition',
  cimbSf3: 'cimbSf3QuantityCondition',
  velocityL: 'velocityLQuantityCondition',
  velocityM: 'velocityMQuantityCondition',
  l2cpQuantity: 'l2cpQuantityCondition',
  l2cpAmountEur: 'l2cpAmountEurCondition',
  l2cpRate: 'l2cpRateCondition',
  l2cpGrossRate: 'l2cpGrossRateCondition',
  l2cpSnpDelta: 'l2cpSnpDeltaCondition',
};

/** Map multi-select column IDs to their LocalInventoryFilters array key (for "Filter by value" context menu). */
export const COLUMN_ID_TO_FILTER_ARRAY_KEY: Partial<Record<string, keyof LocalInventoryFilters>> = {
  accountId: 'accountIds',
  counterparty: 'counterparties',
  currency: 'currencies',
  exchange: 'exchanges',
  caStatus: 'caStatuses',
  caType: 'caTypes',
  brokerAccountName: 'brokerAccountNames',
  brokerSettlementCounterparty: 'brokerSettlementCounterparties',
};

/**
 * Returns true if the given filter has active values in local filters (would affect results).
 */
export const getHasActiveValuesForFilter = (
  filters: LocalInventoryFilters,
  filterId: string
): boolean => {
  if (filterId === 'l2cpGroup') {
    return (
      (filters.l2cpCounterparties?.length ?? 0) > 0 ||
      isNumericConditionActive(filters.l2cpQuantityCondition) ||
      isNumericConditionActive(filters.l2cpAmountEurCondition) ||
      isNumericConditionActive(filters.l2cpRateCondition) ||
      isNumericConditionActive(filters.l2cpGrossRateCondition) ||
      isNumericConditionActive(filters.l2cpSnpDeltaCondition)
    );
  }
  const cKey = CONDITION_KEYS[filterId];
  if (cKey) {
    return isNumericConditionActive(
      filters[cKey] as NumericFilterCondition
    );
  }
  if (filterId === 'accountId') return filters.accountIds.length > 0;
  if (filterId === 'counterparty') return filters.counterparties.length > 0;
  if (filterId === 'currency') return filters.currencies.length > 0;
  if (filterId === 'exchange') return filters.exchanges.length > 0;
  if (filterId === 'caStatus') return filters.caStatuses.length > 0;
  if (filterId === 'caType') return filters.caTypes.length > 0;
  if (filterId === 'brokerAccountName') return filters.brokerAccountNames.length > 0;
  if (filterId === 'brokerSettlementCounterparty') return filters.brokerSettlementCounterparties.length > 0;
  if (filterId === 'exDate') return !!(filters.exDateFrom || filters.exDateTo);
  return false;
};

/**
 * Returns column IDs that currently have an active filter (for header icon highlight).
 */
export const getActiveFilterColumnIds = (
  filters: LocalInventoryFilters
): string[] => {
  const columnIds: string[] = [];
  for (const filterId of Object.keys(FILTER_ID_TO_COLUMN_ID)) {
    if (getHasActiveValuesForFilter(filters, filterId)) {
      const columnId = FILTER_ID_TO_COLUMN_ID[filterId];
      if (columnId && !columnIds.includes(columnId)) {
        columnIds.push(columnId);
      }
    }
  }
  return columnIds;
};
